import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Space,
  Popconfirm,
  message,
  Row,
  Col,
  Statistic,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  TagOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCategories, createMedicine, updateMedicine, createCategory, updateCategory, deleteCategory } from '../store/slices/medicinesSlice';
import { createSellerMedicine, fetchSellerMedicines, deleteSellerMedicine, updateSellerMedicine } from '../store/slices/sellerMedicineSlice';
import { useDebouncedSearch } from '../hooks/useDebounce';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Medicine {
  medicine_id: number;
  seller_medicine_id?: number; // Add this property
  medicine_name: string;
  category_id: number;
  category_name: string;
  description: string;
  created_at: string;
  is_active?: boolean;
  deleted_at?: string;
  // These fields come from seller medicine data
  unit_price?: number;
  quantity?: number;
  expiry_date?: string;
}

const Medicines: React.FC = () => {
  const dispatch = useAppDispatch();
  const medicinesState = useAppSelector((state) => state.medicines);
  const sellerMedicinesState = useAppSelector((state) => state.sellerMedicines);
  const { medicines, categories, loading, pagination } = medicinesState;
  const { sellerMedicines, loading: sellerMedicinesLoading } = sellerMedicinesState;
  
  // Transform seller medicines to medicine format for display
  const transformedMedicines = React.useMemo(() => {
    if (!sellerMedicines || sellerMedicines.length === 0) return [];
    
    return sellerMedicines.map((sellerMedicine: any) => ({
      medicine_id: sellerMedicine.medicine_id || sellerMedicine.seller_medicine_id, // Use seller_medicine_id as fallback
      seller_medicine_id: sellerMedicine.seller_medicine_id, // Keep seller medicine ID for operations
      medicine_name: sellerMedicine.medicine_name || 'Unknown Medicine',
      category_id: sellerMedicine.category_id,
      category_name: sellerMedicine.category_name || 'No Category',
      description: sellerMedicine.description || '',
      created_at: sellerMedicine.created_at,
      is_active: sellerMedicine.is_active,
      // Add pricing and quantity data from seller medicine
      unit_price: sellerMedicine.unit_price,
      quantity: sellerMedicine.seller_stock_quantity,
      expiry_date: sellerMedicine.expiry_date,
    }));
  }, [sellerMedicines]);

  // Debug categories and medicines
  useEffect(() => {
    console.log('Categories from Redux state:', categories);
    console.log('Categories length:', categories?.length);
    console.log('Seller medicines from Redux state:', sellerMedicines);
    console.log('Seller medicines length:', sellerMedicines?.length);
    console.log('Transformed medicines:', transformedMedicines);
    if (transformedMedicines && transformedMedicines.length > 0) {
      console.log('First transformed medicine data:', transformedMedicines[0]);
    }
  }, [categories, sellerMedicines, transformedMedicines]);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [editingMedicine, setEditingMedicine] = useState<Medicine | null>(null);
  const [form] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [searchText, setSearchText] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isAddingCategory, setIsAddingCategory] = useState<boolean>(false);
  
  // Debounce search text to avoid API calls on every keystroke
  const debouncedSearchText = useDebouncedSearch(searchText, 300);

  useEffect(() => {
    // Fetch seller medicines for seller dashboard
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (currentUser.id) {
      dispatch(fetchSellerMedicines({ page: 1, limit: 5, seller_id: currentUser.id }));
    } else {
      console.error('No seller ID found in user data');
    }
  }, [dispatch, debouncedSearchText, selectedCategory]);

  // Fetch categories on component mount
  useEffect(() => {
    console.log('Fetching categories...');
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleAddMedicine = () => {
    // Check if categories exist before allowing medicine creation
    if (!categories || categories.length === 0) {
      message.warning('Please create a category first before adding medicines.');
      setIsCategoryModalVisible(true);
      return;
    }
    
    setEditingMedicine(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditMedicine = (medicine: Medicine) => {
    setEditingMedicine(medicine);
    form.setFieldsValue({
      ...medicine,
      expiry_date: dayjs(medicine.expiry_date),
    });
    setIsModalVisible(true);
  };

  const handleDeleteMedicine = async (medicineId: number) => {
    try {
      console.log('Attempting to delete medicine ID:', medicineId);
      console.log('Current transformed medicines:', transformedMedicines);
      
      // Find the seller medicine ID for this medicine
      const medicine = transformedMedicines.find((med: any) => med.medicine_id === medicineId);
      
      if (!medicine || !medicine.seller_medicine_id) {
        console.log('Seller medicine not found for medicine ID:', medicineId);
        message.error('Seller medicine not found. Please refresh the page and try again.');
        // Refresh the data to get the latest state
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id) {
          dispatch(fetchSellerMedicines({ page: 1, limit: 5, seller_id: currentUser.id }));
        }
        return;
      }

      console.log('Found medicine with seller_medicine_id:', medicine.seller_medicine_id);

      const result = await dispatch(deleteSellerMedicine(medicine.seller_medicine_id));
      
      if (deleteSellerMedicine.fulfilled.match(result)) {
        message.success('Medicine deleted successfully');
        // The Redux state should already be updated by the reducer
        // No need to refresh if the delete was successful
      } else if (deleteSellerMedicine.rejected.match(result)) {
        // Extract error message from the rejected action
        const errorMessage = (result.payload as any)?.message || result.error?.message || 'Failed to delete medicine';
        message.error(errorMessage);
        
        // If the error is "not found", refresh the data
        if (errorMessage.includes('not found')) {
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id) {
          dispatch(fetchSellerMedicines({ page: 1, limit: 5, seller_id: currentUser.id }));
        }
        }
      } else {
        message.error('Failed to delete medicine');
      }
    } catch (error) {
      message.error('Failed to delete medicine');
      // Refresh data on error
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser.id) {
        dispatch(fetchSellerMedicines({ page: 1, limit: 5, seller_id: currentUser.id }));
      }
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setIsAddingCategory(false);
    setIsCategoryModalVisible(true);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    categoryForm.setFieldsValue(category);
    setIsCategoryModalVisible(true);
  };

  const handleDeleteCategory = async (categoryId: number) => {
    try {
      const result = await dispatch(deleteCategory(categoryId));
      if (deleteCategory.fulfilled.match(result)) {
        message.success('Category deleted successfully');
        // Refresh categories list
        dispatch(fetchCategories());
      } else {
        message.error('Failed to delete category');
      }
    } catch (error) {
      message.error('Failed to delete category');
    }
  };


  const handleCategoryModalOk = async () => {
    try {
      const values = await categoryForm.validateFields();
      if (editingCategory) {
        const result = await dispatch(updateCategory({ id: editingCategory.category_id, categoryData: values }));
        if (updateCategory.fulfilled.match(result)) {
          message.success('Category updated successfully');
          setIsCategoryModalVisible(false);
          categoryForm.resetFields();
          setEditingCategory(null);
          // Refresh categories list
          dispatch(fetchCategories());
        } else {
          message.error('Failed to update category');
        }
      } else {
        const result = await dispatch(createCategory(values));
        if (createCategory.fulfilled.match(result)) {
          message.success('Category created successfully');
          setIsCategoryModalVisible(false);
          categoryForm.resetFields();
          // Refresh categories list
          dispatch(fetchCategories());
          // If we were adding a category from the dropdown, set it as selected
          if (isAddingCategory) {
            form.setFieldsValue({ category_id: result.payload.category_id });
            setIsAddingCategory(false);
          }
        } else {
          message.error('Failed to create category');
        }
      }
    } catch (error) {
      message.error('Please fill in all required fields');
    }
  };

  const handleQuickAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setIsAddingCategory(true);
    setIsCategoryModalVisible(true);
  };


  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Validate category selection
      if (!values.category_id) {
        message.error('Please select a category for the medicine');
        return;
      }
      
      const formattedValues = {
        ...values,
        expiry_date: values.expiry_date.format('YYYY-MM-DD'),
        category_id: values.category_id, // Ensure category_id is included
      };
      
      if (editingMedicine) {
        // Find the seller medicine for this medicine
        const medicine = transformedMedicines.find((med: any) => med.medicine_id === editingMedicine.medicine_id);
        
        if (!medicine || !medicine.seller_medicine_id) {
          message.error('Seller medicine not found');
          return;
        }

        // Update both medicine and seller medicine
        const medicineResult = await dispatch(updateMedicine({ id: editingMedicine.medicine_id, medicineData: formattedValues }));
        
        if (updateMedicine.fulfilled.match(medicineResult)) {
          // Update seller medicine with pricing/quantity data
          const sellerMedicineData = {
            unit_price: formattedValues.unit_price || 0,
            seller_stock_quantity: formattedValues.quantity || 0,
            expiry_date: formattedValues.expiry_date,
          };
          
          const sellerResult = await dispatch(updateSellerMedicine({ 
            id: medicine.seller_medicine_id, 
            sellerMedicineData 
          }));
          
          if (updateSellerMedicine.fulfilled.match(sellerResult)) {
            message.success('Medicine updated successfully');
            setIsModalVisible(false);
            form.resetFields();
            setEditingMedicine(null);
            // Refresh the seller medicines list
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id) {
          dispatch(fetchSellerMedicines({ page: 1, limit: 5, seller_id: currentUser.id }));
        }
          } else {
            message.error('Failed to update seller medicine data');
          }
        } else if (updateMedicine.rejected.match(medicineResult)) {
          const errorMessage = (medicineResult.payload as any)?.message || medicineResult.error?.message || 'Failed to update medicine';
          message.error(errorMessage);
        } else {
          message.error('Failed to update medicine');
        }
      } else {
        const result = await dispatch(createMedicine(formattedValues));
        if (createMedicine.fulfilled.match(result)) {
          // Create seller medicine entry after successful medicine creation
          const createdMedicine = result.payload.data || result.payload;
          const medicineId = createdMedicine.id;
          
          // Generate a unique batch number
          const batchNumber = `BATCH_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          
          // Get current user ID from localStorage
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          
          const sellerMedicineData = {
            batch_number: batchNumber,
            seller: currentUser.id, // Get seller ID from logged-in user
            medicine: medicineId,
            unit_price: formattedValues.unit_price || 0,
            seller_stock_quantity: formattedValues.quantity || 0,
            expiry_date: formattedValues.expiry_date,
            discount_percentage: 0,
            hike_percent: 0,
            is_active: true,
            destroyed_quantity: 0,
            expired_quantity: 0,
          };
          
          try {
            await dispatch(createSellerMedicine(sellerMedicineData));
            console.log('Seller medicine created successfully');
          } catch (error) {
            console.error('Failed to create seller medicine:', error);
            // Don't show error to user as medicine was created successfully
          }
          
          message.success('Medicine created successfully');
          setIsModalVisible(false);
          form.resetFields();
          // Refresh the seller medicines list to show the new medicine
          if (currentUser.id) {
            dispatch(fetchSellerMedicines({ page: 1, limit: 5, seller_id: currentUser.id }));
          }
        } else {
          message.error('Failed to create medicine');
        }
      }
    } catch (error) {
      message.error('Please fill in all required fields');
    }
  };

  const columns = [
    {
      title: 'Medicine Name',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      render: (text: string) => (
        <Space>
          <MedicineBoxOutlined />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (text: string) => (
        <Tag color="blue" icon={<TagOutlined />}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          {new Date(date).toLocaleDateString()}
        </Space>
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (price: number) => {
        const numericPrice = typeof price === 'string' ? parseFloat(price) : price || 0;
        return `₹${numericPrice.toFixed(2)}`;
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number) => (
        <Space>
          <MedicineBoxOutlined />
          {quantity}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Medicine) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditMedicine(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this medicine?"
            onConfirm={() => handleDeleteMedicine(record.medicine_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];


  const totalValue = (transformedMedicines || []).reduce((sum, medicine) => {
    const unitPrice = typeof medicine.unit_price === 'string' ? parseFloat(medicine.unit_price) : medicine.unit_price || 0;
    const quantity = typeof medicine.quantity === 'string' ? parseFloat(medicine.quantity) : medicine.quantity || 0;
    return sum + (unitPrice * quantity);
  }, 0);
  const expiringSoon = (transformedMedicines || []).filter(medicine => {
    if (!medicine.expiry_date) return false;
    const expiry = new Date(medicine.expiry_date);
    const now = new Date();
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays >= 0;
  }).length;

  return (
    <div>
      <style>{`
        .ant-select-dropdown .category-actions {
          opacity: 0.7;
          transition: opacity 0.2s;
        }
        .ant-select-dropdown .ant-select-item:hover .category-actions {
          opacity: 1;
        }
        .ant-select-dropdown .category-actions .ant-btn:hover {
          background-color: rgba(0, 0, 0, 0.04);
        }
        .medicines-title {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }} className="medicines-title">
          Medicines Management
        </Title>
        <Space>
          <Button
            type="default"
            icon={<TagOutlined />}
            onClick={handleAddCategory}
            size="large"
          >
            Add Category
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddMedicine}
            size="large"
            disabled={!categories || categories.length === 0}
          >
            Add New Medicine
          </Button>
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Medicines"
              value={medicines.length}
              prefix={<MedicineBoxOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Categories"
              value={categories.length}
              prefix={<TagOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Value"
              value={totalValue}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Expiring Soon"
              value={expiringSoon}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Medicines Table */}
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Input
            placeholder="Search medicines by name..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 300 }}
            allowClear
            size="middle"
          />
          <Select
            placeholder="Filter by category"
            value={selectedCategory || undefined}
            onChange={setSelectedCategory}
            style={{ minWidth: 200 }}
            allowClear
            showSearch
            size="middle"
            loading={loading}
            filterOption={(input, option) => {
              const label = option?.label || option?.children;
              if (typeof label === 'string') {
                return label.toLowerCase().includes(input.toLowerCase());
              }
              return false;
            }}
            dropdownRender={(menu) => (
              <div>
                {menu}
                <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={handleAddCategory}
                    style={{ width: '100%', textAlign: 'left', fontWeight: 500 }}
                  >
                    Add New Category
                  </Button>
                </div>
              </div>
            )}
          >
            {categories.map(category => {
              const categoryId = category.category_id;
              const medicineCount = medicines.filter(med => med.category_id === categoryId).length;
              return (
                <Option key={category.category_id} value={category.category_id?.toString()}>
                  <div 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      width: '100%',
                      padding: '4px 0'
                    }}
                  >
                    <span style={{ flex: 1, marginRight: '8px' }}>
                      <span style={{ fontWeight: 500 }}>{category.category_name}</span>
                      <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
                        ({medicineCount} medicines)
                      </span>
                    </span>
                    <div 
                      style={{ 
                        display: 'flex', 
                        gap: '4px',
                        opacity: 0.7,
                        transition: 'opacity 0.2s'
                      }}
                      className="category-actions"
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCategory(category);
                        }}
                        style={{ 
                          padding: '2px 4px', 
                          minWidth: 'auto',
                          color: '#1890ff'
                        }}
                        title="Edit category"
                      />
                      <Popconfirm
                        title="Are you sure you want to delete this category?"
                        description="This action cannot be undone. All medicines in this category will be affected."
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          handleDeleteCategory(category.category_id);
                        }}
                        onCancel={(e) => e?.stopPropagation()}
                        okText="Delete"
                        cancelText="Cancel"
                        okType="danger"
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          danger
                          onClick={(e) => e.stopPropagation()}
                          style={{ 
                            padding: '2px 4px', 
                            minWidth: 'auto'
                          }}
                          title="Delete category"
                        />
                      </Popconfirm>
                    </div>
                  </div>
                </Option>
              );
            })}
          </Select>
        </div>
        
        
        <Table
          columns={columns}
          dataSource={(transformedMedicines || []) as Medicine[]}
          rowKey="medicine_id"
          loading={loading || sellerMedicinesLoading}
          scroll={{ x: 800 }}
          size="small"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            defaultPageSize: 5,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} medicines`,
            onChange: (page, pageSize) => {
              const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
              if (currentUser.id) {
                dispatch(fetchSellerMedicines({ page, limit: pageSize, seller_id: currentUser.id }));
              }
            },
            onShowSizeChange: (current, size) => {
              const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
              if (currentUser.id) {
                dispatch(fetchSellerMedicines({ page: 1, limit: size, seller_id: currentUser.id }));
              }
            },
          }}
        />
      </Card>

      {/* Add/Edit Medicine Modal */}
      <Modal
        title={editingMedicine ? 'Edit Medicine' : 'Add New Medicine'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={800}
        okButtonProps={{
          disabled: categories.length === 0
        }}
      >
        {categories.length === 0 && (
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#fff7e6', 
            border: '1px solid #ffd591', 
            borderRadius: '6px', 
            marginBottom: '16px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TagOutlined style={{ color: '#fa8c16' }} />
              <span style={{ color: '#d46b08', fontWeight: 500 }}>
                No categories available. Please create a category first before adding medicines.
              </span>
            </div>
            <Button 
              type="link" 
              onClick={() => {
                setIsModalVisible(false);
                setIsCategoryModalVisible(true);
              }}
              style={{ padding: '4px 0', marginTop: '8px' }}
            >
              Create Category Now
            </Button>
          </div>
        )}
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="medicine_name"
                label="Medicine Name"
                rules={[{ required: true, message: 'Please enter medicine name' }]}
              >
                <Input placeholder="Enter medicine name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category_id"
                label="Category"
                rules={[
                  { required: true, message: 'Please select category' },
                  { 
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(new Error('Category is required'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Select 
                  placeholder={categories.length === 0 ? "No categories available - Create one first" : "Select or search category"}
                  showSearch
                  size="middle"
                  disabled={categories.length === 0}
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children;
                    if (typeof label === 'string') {
                      return label.toLowerCase().includes(input.toLowerCase());
                    }
                    return false;
                  }}
                  dropdownRender={(menu) => (
                    <div>
                      {menu}
                      <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                        <Button 
                          type="text" 
                          icon={<PlusOutlined />}
                          onClick={handleQuickAddCategory}
                          style={{ width: '100%', textAlign: 'left', fontWeight: 500 }}
                        >
                          Add New Category
                        </Button>
                      </div>
                    </div>
                  )}
                >
                  {categories.map(category => {
                    const categoryId = category.category_id;
                    const medicineCount = medicines.filter(med => med.category_id === categoryId).length;
                    return (
                      <Option key={category.category_id} value={category.category_id}>
                        <div 
                          style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            width: '100%',
                            padding: '4px 0'
                          }}
                        >
                          <span style={{ flex: 1, marginRight: '8px' }}>
                            <span style={{ fontWeight: 500 }}>{category.category_name}</span>
                            <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
                              ({medicineCount} medicines)
                            </span>
                          </span>
                          <div 
                            style={{ 
                              display: 'flex', 
                              gap: '4px',
                              opacity: 0.7,
                              transition: 'opacity 0.2s'
                            }}
                            className="category-actions"
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditCategory(category);
                              }}
                              style={{ 
                                padding: '2px 4px', 
                                minWidth: 'auto',
                                color: '#1890ff'
                              }}
                              title="Edit category"
                            />
                            <Popconfirm
                              title="Are you sure you want to delete this category?"
                              description="This action cannot be undone. All medicines in this category will be affected."
                              onConfirm={(e) => {
                                e?.stopPropagation();
                                handleDeleteCategory(category.category_id);
                              }}
                              onCancel={(e) => e?.stopPropagation()}
                              okText="Delete"
                              cancelText="Cancel"
                              okType="danger"
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                danger
                                onClick={(e) => e.stopPropagation()}
                                style={{ 
                                  padding: '2px 4px', 
                                  minWidth: 'auto'
                                }}
                                title="Delete category"
                              />
                            </Popconfirm>
                          </div>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="expiry_date"
                label="Expiry Date"
                rules={[{ required: true, message: 'Please select expiry date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="unit_price"
                label="Unit Price"
                rules={[{ required: true, message: 'Please enter unit price' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter unit price"
                  min={0}
                  precision={2}
                  prefix="₹"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter quantity"
                  min={0}
                  precision={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter description" />
          </Form.Item>
        </Form>
        </Modal>

      {/* Add/Edit Category Modal */}
      <Modal
        title={editingCategory ? "Edit Category" : (isAddingCategory ? "Add New Category (Quick)" : "Add New Category")}
        open={isCategoryModalVisible}
        onOk={handleCategoryModalOk}
        okText={editingCategory ? "Update" : "Create"}
        onCancel={() => {
          setIsCategoryModalVisible(false);
          setEditingCategory(null);
          setIsAddingCategory(false);
          categoryForm.resetFields();
        }}
        width={500}
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleCategoryModalOk}
        >
          <Form.Item
            name="category_name"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <TextArea rows={3} placeholder="Enter category description" />
          </Form.Item>
        </Form>
        </Modal>
    </div>
  );
};

export default Medicines;
